/**
 * Media filtering and UI utilities powered by MixItUp.
 *
 * This module is responsible for:
 * - Initializing MixItUp
 * - Handling year-based filtering
 * - Updating border styles dynamically based on visible items
 *
 * @author Dreamers of Day
 */

import mixitup from 'mixitup';

/**
 * Updates the border styling for all currently visible media items.
 *
 * The function:
 * - Selects all `.mix` elements that are NOT display:none
 * - Applies a bottom border to every visible item
 * - Removes the bottom border from the last visible item only
 *
 * This ensures consistent visual separation without double borders.
 *
 * @returns {void}
 */
function updateLastItemStyle() {
    // Query for all visible mix items by excluding inline display:none styles
    const visibleItems = document.querySelectorAll(
        '.mix:not([style*="display: none"])',
    );

    // Loop over each visible item
    visibleItems.forEach((item) => {
        // Apply border styles to every item by default
        item.classList.add('border-b', 'border-solid', 'border-gray-300');
    });

    // Convert NodeList to Array so we can safely access the last element
    const lastVisibleItem = Array.from(visibleItems).slice(-1)[0];

    // Guard clause: only proceed if a last item exists
    if (!lastVisibleItem) {
        return;
    }

    // Remove border styles from the final visible item
    // This prevents an unnecessary trailing divider in the UI
    lastVisibleItem.classList.remove(
        'border-b',
        'border-solid',
        'border-gray-300',
    );
}

/**
 * Initializes MixItUp media filtering and wires up UI interactions.
 *
 * Responsibilities:
 * - Locate the media container
 * - Initialize MixItUp with default sorting
 * - React to filter changes (year selector)
 * - Recalculate UI borders after every mix operation
 *
 * @returns {void}
 */
function filterMedia() {
    // Locate the MixItUp container element
    const containerElement = document.querySelector('.js-media-filter');

    // If the container does not exist, abort early to avoid runtime errors
    if (!containerElement) {
        return;
    }

    // Initialize MixItUp on the container
    const mixerInstance = mixitup(containerElement, {
        load: {
            // Sort by published date descending on initial load
            sort: 'published-date:desc',
        },
        callbacks: {
            /**
             * Fired after MixItUp completes a filtering or sorting operation.
             *
             * We use this hook to reapply border logic because DOM visibility
             * has changed at this point.
             */
            onMixEnd() {
                updateLastItemStyle();
            },
        },
    });

    // Apply border logic immediately after initialization
    updateLastItemStyle();

    // Locate the year select dropdown
    const yearSelectElement = document.getElementById('yearSelect');

    // Only attach listeners if the select element exists
    if (!yearSelectElement) {
        return;
    }

    // Listen for changes on the year selector
    yearSelectElement.addEventListener('change', function handleYearChange() {
        // Capture the selected year value from the dropdown
        const selectedYear = this.value;

        // If "all" is selected, remove all filters
        if (selectedYear === 'all') {
            mixerInstance.filter('all');
        } else {
            // Otherwise, filter items whose data-published-date
            // attribute starts with the selected year
            mixerInstance.filter(`[data-published-date^="${selectedYear}"]`);
        }

        // Reapply border logic after filtering
        updateLastItemStyle();
    });
}

// Export the filterMedia initializer as the default module export
export default filterMedia;
